import { useEffect, useRef } from 'react';
import clsx from 'clsx';

import type { FormChecklistItem } from '@/features/tasks/task-dialogs/fields/FormChecklistBlock';
import type { UseVoiceRecorder } from '@/pages/task-redesign/model/useVoiceRecorder';
import { FloatingPanel } from '@/shared/ui/FloatingPanel';

import type { PersonItem } from '../../types';
import { onAltArrowDownOpen } from '../../utils';
import {
  formatVoiceLength,
  RDocChip,
  RMediaTile,
  RVoicePill,
  isVideoName,
} from './attachments';
import {
  apiFileUrl,
  formatHours,
  parseHours,
  splitFiles,
} from './checklistModel';
import { RAttachMenu, type AttachKind } from './RAttachMenu';
import { RVoicePanel } from './RVoicePanel';
import { RPersonAvatar } from './RPersonAvatar';
import { RSearch } from './icons';

export type ItemCtx = {
  people: PersonItem[];
  /** Скрыть соисполнителей пункта: у создания задачи v2 их нет в контракте. */
  hideMembers?: boolean;
  disabled?: boolean;
  peopleQuery: string;
  setPeopleQuery: (v: string) => void;
  textEdit: { id: number; draft: string } | null;
  setTextEdit: (v: { id: number; draft: string } | null) => void;
  hoursEdit: { id: number; draft: string } | null;
  setHoursEdit: (v: { id: number; draft: string } | null) => void;
  actionsFor: number | null;
  setActionsFor: (v: number | null) => void;
  fileMenuFor: number | null;
  setFileMenuFor: (v: number | null) => void;
  pickerFor: number | null;
  setPickerFor: (v: number | null) => void;
  recFor: number | null;
  recorder: UseVoiceRecorder;
  startRec: (id: number) => void;
  patch: (
    id: number,
    fn: (i: FormChecklistItem) => Partial<FormChecklistItem>
  ) => void;
  remove: (id: number) => void;
  addSub: (id: number) => void;
  addNote: (id: number) => void;
  pickFiles: (id: number, kind: AttachKind) => void;
};

/** Автоподстройка высоты textarea под содержимое (как grow() в прототипе). */
function useGrow(enabled: boolean) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    el.focus();
    el.select();
  }, [enabled]);
  return ref;
}

/** Пункт чек-листа (и подпункт — те же элементы в уменьшенных размерах). */
export function RChecklistRow({
  item,
  subs,
  ctx,
  sub = false,
}: {
  item: FormChecklistItem;
  subs: FormChecklistItem[];
  ctx: ItemCtx;
  sub?: boolean;
}) {
  const {
    people,
    hideMembers,
    disabled,
    peopleQuery,
    setPeopleQuery,
    textEdit,
    setTextEdit,
    hoursEdit,
    setHoursEdit,
    actionsFor,
    setActionsFor,
    fileMenuFor,
    setFileMenuFor,
    pickerFor,
    setPickerFor,
    recFor,
    recorder,
    startRec,
    patch,
    remove,
    addSub,
    addNote,
    pickFiles,
  } = ctx;

  const editingText = textEdit?.id === item.id;
  const editingHours = hoursEdit?.id === item.id;
  const textRef = useGrow(editingText);
  const rowRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLSpanElement>(null);

  const files = splitFiles(item.files);
  const notes = item.notes ?? [];
  const members = item.members ?? [];
  const hours = parseHours(item.hours);
  const hoursLabel = item.hours ? `${formatHours(hours)} ч` : 'Время';

  const doneSubs = subs.filter((s) => s.isComplete).length;
  const peopleById = new Map(people.map((p) => [p.id, p]));
  const filteredPeople = peopleQuery.trim()
    ? people.filter((p) =>
        p.name.toLowerCase().includes(peopleQuery.trim().toLowerCase())
      )
    : people;

  const removeFile = (name: string, content: string) =>
    patch(item.id, (i) => ({
      files: (i.files ?? []).filter(
        (f) => !(f.name === name && f.content === content)
      ),
    }));

  const padLeft = sub ? 'pl-[26px]' : 'pl-[28px]';

  return (
    <div
      ref={rowRef}
      className={clsx(
        'relative flex flex-wrap items-start rounded-[16px] transition-colors hover:bg-[rgba(59,77,127,0.045)]',
        sub
          ? 'gap-y-[4px] rounded-[14px] px-[8px] py-[6px]'
          : 'mb-[2px] gap-y-[4px] px-[12px] py-[9px]'
      )}
      style={{
        zIndex:
          actionsFor === item.id ||
          pickerFor === item.id ||
          fileMenuFor === item.id ||
          recFor === item.id
            ? 5
            : undefined,
      }}
    >
      {sub ? (
        <span className="absolute -left-[27px] top-[3px] h-[12px] w-[27px] rounded-bl-[8px] border-b-2 border-l-2 border-[rgba(59,77,127,0.22)]" />
      ) : null}
      {!sub && subs.length > 0 ? (
        <span
          className="absolute left-[21px] top-[30px] bottom-[66px] w-[2px] rounded-[2px]"
          style={{
            background:
              'linear-gradient(180deg, rgba(59,77,127,0.05), rgba(59,77,127,0.22) 12%, rgba(59,77,127,0.22) 75%, rgba(59,77,127,0))',
          }}
        />
      ) : null}

      {/* Галочка и текст */}
      <div
        className={clsx(
          'order-1 flex min-w-0 items-start',
          sub ? 'flex-[1_1_180px] gap-[9px]' : 'flex-[1_1_220px] gap-[10px]'
        )}
      >
        <button
          type="button"
          disabled={disabled}
          title="Отметить"
          onClick={() => patch(item.id, (i) => ({ isComplete: !i.isComplete }))}
          className={clsx(
            'mt-[1px] flex shrink-0 items-center justify-center rounded-full border-[1.5px] hover:border-[#152242]',
            sub
              ? 'h-[17px] w-[17px] flex-[0_0_17px]'
              : 'h-[19px] w-[19px] flex-[0_0_19px]'
          )}
          style={{
            borderColor: item.isComplete ? '#57B792' : 'rgba(46,58,94,0.25)',
            background: item.isComplete ? '#57B792' : '#FFFFFF',
          }}
        >
          <svg
            width={sub ? 10 : 11}
            height={sub ? 10 : 11}
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden
          >
            <path
              d="M3.6 8.4l3 3 5.8-6.2"
              stroke={item.isComplete ? '#FFFFFF' : 'transparent'}
              strokeWidth={sub ? 2.2 : 2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {editingText ? (
          <textarea
            ref={textRef}
            rows={1}
            value={textEdit.draft}
            onChange={(e) => {
              const el = e.currentTarget;
              el.style.height = 'auto';
              el.style.height = `${el.scrollHeight}px`;
              setTextEdit({ id: item.id, draft: e.target.value });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                e.currentTarget.blur();
              }
              if (e.key === 'Escape') {
                setTextEdit(null);
              }
            }}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v) {
                patch(item.id, () => ({ title: v }));
              }
              setTextEdit(null);
            }}
            className={clsx(
              'min-w-0 flex-1 resize-none overflow-hidden rounded-[10px] border border-[rgba(59,77,127,0.4)] bg-white font-sans leading-[1.45] text-[#141E31] outline-none',
              sub
                ? 'rounded-[9px] px-[9px] py-[4px] text-[11.5px]'
                : 'px-[10px] py-[5px] text-[12px]'
            )}
          />
        ) : (
          <span
            title="Двойной клик — изменить"
            onDoubleClick={() =>
              !disabled && setTextEdit({ id: item.id, draft: item.title })
            }
            className={clsx(
              'min-w-0 flex-1 cursor-text break-words leading-[1.45]',
              sub ? 'text-[11.5px]' : 'text-[12px]'
            )}
            style={{
              color: item.isComplete ? 'rgba(20,30,49,0.4)' : '#575757',
              textDecoration: item.isComplete ? 'line-through' : 'none',
              overflowWrap: 'anywhere',
            }}
          >
            {item.title}
          </span>
        )}
      </div>

      {/* Время и меню функций */}
      <div
        className={clsx(
          'order-2 flex flex-[0_0_auto] flex-col items-end gap-[2px] self-start',
          sub ? 'ml-[8px]' : 'ml-[10px]'
        )}
      >
        {editingHours ? (
          <span className="flex flex-[0_0_auto] items-center gap-[4px]">
            <input
              autoFocus
              value={hoursEdit.draft}
              placeholder="0"
              onChange={(e) =>
                setHoursEdit({ id: item.id, draft: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
                if (e.key === 'Escape') {
                  setHoursEdit(null);
                }
              }}
              onBlur={(e) => {
                patch(item.id, () => ({ hours: e.target.value.trim() }));
                setHoursEdit(null);
              }}
              className={clsx(
                'rounded-full border border-[rgba(59,77,127,0.4)] text-right font-bold text-[#575757] outline-none',
                sub
                  ? 'w-[44px] px-[9px] py-[3px] text-[10.5px]'
                  : 'w-[48px] px-[10px] py-[4px] text-[11px]'
              )}
            />
            <span
              className={clsx(
                'font-semibold text-[rgba(44,49,60,0.55)]',
                sub ? 'text-[10.5px]' : 'text-[11px]'
              )}
            >
              ч
            </span>
          </span>
        ) : (
          <button
            type="button"
            disabled={disabled}
            title={sub ? 'Время подпункта' : 'Время пункта'}
            onClick={() => {
              setHoursEdit({ id: item.id, draft: item.hours ?? '' });
              setActionsFor(null);
            }}
            className={clsx(
              'flex flex-[0_0_auto] items-center whitespace-nowrap bg-white font-medium shadow-[0_1px_3px_rgba(20,30,49,0.08)]',
              sub
                ? 'gap-[5px] rounded-[9px] px-[10px] py-[5px] text-[10.5px]'
                : 'gap-[6px] rounded-[10px] px-[12px] py-[6px] text-[11px]'
            )}
            style={{ color: item.hours ? '#141E31' : 'rgba(20,30,49,0.45)' }}
          >
            <svg
              width={sub ? 11 : 12}
              height={sub ? 11 : 12}
              viewBox="0 0 16 16"
              fill="none"
              className="shrink-0"
              aria-hidden
            >
              <circle
                cx="8"
                cy="8"
                r="6"
                stroke={item.hours ? '#3B4D7F' : 'rgba(20,30,49,0.35)'}
                strokeWidth={1.5}
              />
              <path
                d="M8 4.8V8l2.2 1.6"
                stroke={item.hours ? '#3B4D7F' : 'rgba(20,30,49,0.35)'}
                strokeWidth={1.6}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {hoursLabel}
          </button>
        )}

        <span ref={actionsRef} className="relative flex">
          <button
            type="button"
            disabled={disabled}
            title={sub ? 'Функции подпункта' : 'Функции пункта'}
            onClick={() =>
              setActionsFor(actionsFor === item.id ? null : item.id)
            }
            onKeyDown={onAltArrowDownOpen(actionsFor === item.id, () =>
              setActionsFor(item.id)
            )}
            className="flex h-[28px] w-[28px] items-center justify-center rounded-[9px] hover:bg-[rgba(59,77,127,0.07)]"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="#6A6A6A"
              aria-hidden
            >
              <circle cx="5.5" cy="12" r="2.1" />
              <circle cx="12" cy="12" r="2.1" />
              <circle cx="18.5" cy="12" r="2.1" />
            </svg>
          </button>

          {actionsFor === item.id ? (
            <FloatingPanel
              anchorRef={actionsRef}
              align="end"
              topOffset={30}
              className="flex items-center gap-[2px] rounded-[11px] border border-[rgba(59,77,127,0.12)] bg-white p-[4px] shadow-[0_12px_28px_rgba(20,30,49,0.16)]"
            >
              {!sub ? (
                <button
                  type="button"
                  title="Подпункты"
                  onClick={() => addSub(item.id)}
                  className="relative inline-flex h-[26px] w-[26px] flex-[0_0_26px] items-center justify-center rounded-[8px] hover:bg-[rgba(59,77,127,0.07)]"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={subs.length ? '#57B792' : '#3B4D7F'}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M9 11.5l2.5 2.5L21 4.5" />
                    <path d="M20.5 12.5V19a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h11" />
                  </svg>
                  {subs.length > 0 ? (
                    <span className="absolute -right-[6px] -top-[6px] flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-[#57B792] px-[4px] text-[10px] font-bold text-white">
                      {doneSubs}/{subs.length}
                    </span>
                  ) : null}
                </button>
              ) : null}

              <button
                type="button"
                title="Файлы"
                onClick={() => {
                  setFileMenuFor(item.id);
                  setActionsFor(null);
                }}
                className="relative inline-flex h-[26px] w-[26px] flex-[0_0_26px] items-center justify-center rounded-[8px] hover:bg-[rgba(59,77,127,0.07)]"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={(item.files ?? []).length ? '#3B4D7F' : '#6A7590'}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
                {(item.files ?? []).length > 0 ? (
                  <span className="absolute -right-[6px] -top-[6px] flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-[#3B4D7F] px-[4px] text-[10px] font-bold text-white">
                    {(item.files ?? []).length}
                  </span>
                ) : null}
              </button>

              <button
                type="button"
                title="Комментарии"
                onClick={() => addNote(item.id)}
                className="relative inline-flex h-[26px] w-[26px] flex-[0_0_26px] items-center justify-center rounded-[8px] hover:bg-[rgba(59,77,127,0.07)]"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={notes.length ? '#EF4642' : '#6A7590'}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M21 14.5a2 2 0 0 1-2 2H7.5L3.5 20.5V5a2 2 0 0 1 2-2H19a2 2 0 0 1 2 2z" />
                </svg>
                {notes.length > 0 ? (
                  <span className="absolute -right-[6px] -top-[6px] flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-[#EF4642] px-[4px] text-[10px] font-bold text-white">
                    {notes.length}
                  </span>
                ) : null}
              </button>

              <button
                type="button"
                title="Записать голосовое"
                onClick={() => startRec(item.id)}
                className="inline-flex h-[26px] w-[26px] flex-[0_0_26px] items-center justify-center rounded-[8px] hover:bg-[rgba(59,77,127,0.07)]"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#E59D02"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M12 3.5a2.6 2.6 0 0 1 2.6 2.6v5.4a2.6 2.6 0 0 1-5.2 0V6.1A2.6 2.6 0 0 1 12 3.5zM5.8 11.2a6.2 6.2 0 0 0 12.4 0M12 17.6V21" />
                </svg>
              </button>

              {hideMembers ? null : (
                <button
                  type="button"
                  title="Соисполнитель"
                  onClick={() => {
                    setPickerFor(item.id);
                    setActionsFor(null);
                  }}
                  onKeyDown={onAltArrowDownOpen(pickerFor === item.id, () => {
                    setPickerFor(item.id);
                    setActionsFor(null);
                  })}
                  className="inline-flex h-[26px] w-[26px] flex-[0_0_26px] items-center justify-center rounded-[8px] hover:bg-[rgba(59,77,127,0.07)]"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={members.length ? '#57B792' : '#6A7590'}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <circle cx="12" cy="8" r="3.6" />
                    <path d="M4.8 20.5a7.2 7.2 0 0 1 14.4 0" />
                  </svg>
                </button>
              )}

              <span className="mx-[2px] h-[16px] w-px bg-[rgba(59,77,127,0.12)]" />

              <button
                type="button"
                title={sub ? 'Удалить подпункт' : 'Удалить пункт'}
                onClick={() => remove(item.id)}
                className="inline-flex h-[26px] w-[26px] flex-[0_0_26px] items-center justify-center rounded-[8px] hover:bg-[rgba(239,70,66,0.10)]"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#EF4642"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M4 7h16M9.5 7V5h5v2M6.5 7l.8 12.2a1.8 1.8 0 0 0 1.8 1.7h5.8a1.8 1.8 0 0 0 1.8-1.7L17.5 7" />
                </svg>
              </button>
            </FloatingPanel>
          ) : null}

          {pickerFor === item.id ? (
            <FloatingPanel
              anchorRef={actionsRef}
              align="end"
              topOffset={26}
              className="block max-h-[260px] w-[236px] overflow-y-auto rounded-[14px] bg-white p-[6px] shadow-[0_18px_40px_rgba(20,30,49,0.22)]"
            >
              <span className="mx-[2px] mb-[6px] mt-[2px] flex items-center gap-[8px] rounded-[10px] bg-[#F1F3F8] px-[10px] py-[7px]">
                <RSearch />
                <input
                  autoFocus
                  value={peopleQuery}
                  onChange={(e) => setPeopleQuery(e.target.value)}
                  placeholder="Поиск по имени"
                  className="min-w-0 flex-1 bg-transparent text-[11.5px] text-[#575757] outline-none"
                />
              </span>
              {filteredPeople.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    patch(item.id, (i) => {
                      const cur = i.members ?? [];
                      return {
                        members: cur.includes(p.id)
                          ? cur.filter((x) => x !== p.id)
                          : [...cur, p.id],
                      };
                    });
                    setPickerFor(null);
                  }}
                  className="flex w-full items-center gap-[9px] rounded-[10px] px-[9px] py-[8px] text-left hover:bg-[rgba(59,77,127,0.06)]"
                >
                  <RPersonAvatar
                    id={p.id}
                    name={p.name}
                    avatarUrl={p.avatarUrl}
                  />
                  <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-[#141E31]">
                    {p.name}
                  </span>
                </button>
              ))}
            </FloatingPanel>
          ) : null}
        </span>
      </div>

      {/* Меню файлов и запись голосового — у правого верхнего угла строки. */}
      {fileMenuFor === item.id ? (
        <RAttachMenu
          anchorRef={rowRef}
          align="end"
          topOffset={30}
          alignOffset={46}
          onPick={(kind) => pickFiles(item.id, kind)}
        />
      ) : null}

      {recFor === item.id ? (
        <RVoicePanel
          anchorRef={rowRef}
          align="end"
          topOffset={30}
          alignOffset={24}
          clipId={`recclip-item-${item.id}`}
          elapsedMs={recorder.elapsedMs}
          busy={recorder.state === 'sending'}
          onCancel={recorder.cancel}
          onSave={recorder.stopAndSend}
        />
      ) : null}

      {/* Полосы вложений, комментариев и соисполнителей */}
      {files.media.length > 0 ? (
        <span
          className={clsx(
            'order-3 mt-[8px] flex w-full flex-wrap items-start gap-[8px]',
            padLeft
          )}
        >
          {files.media.map((f) => (
            <RMediaTile
              key={`${f.name}_${f.content.slice(0, 16)}`}
              name={f.name}
              video={isVideoName(f.name)}
              previewUrl={apiFileUrl(f)}
              onRemove={() => removeFile(f.name, f.content)}
            />
          ))}
        </span>
      ) : null}

      {files.voices.length > 0 ? (
        <span
          className={clsx(
            'order-5 mt-[8px] flex w-full flex-col gap-[6px]',
            padLeft
          )}
        >
          {files.voices.map((f) => (
            <RVoicePill
              key={`${f.name}_${f.content.slice(0, 16)}`}
              length={formatVoiceLength(f.durationMs ?? 0)}
              src={apiFileUrl(f)}
              onRemove={() => removeFile(f.name, f.content)}
            />
          ))}
        </span>
      ) : null}

      {files.docs.length > 0 ? (
        <span
          className={clsx(
            'order-6 mt-[7px] flex w-full flex-wrap items-center gap-[6px]',
            padLeft
          )}
        >
          {files.docs.map((f) => (
            <RDocChip
              key={`${f.name}_${f.content.slice(0, 16)}`}
              name={f.name}
              onRemove={() => removeFile(f.name, f.content)}
            />
          ))}
        </span>
      ) : null}

      {notes.length > 0 ? (
        <span
          className={clsx(
            'order-7 mt-[7px] flex w-full flex-col gap-[5px]',
            padLeft
          )}
        >
          {notes.map((n) => (
            <span
              key={n.id}
              className="flex items-center gap-[8px] rounded-[10px] bg-white px-[10px] py-[6px] shadow-[0_1px_3px_rgba(20,30,49,0.08)]"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#EF4642"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
                aria-hidden
              >
                <path d="M21 14.5a2 2 0 0 1-2 2H7.5L3.5 20.5V5a2 2 0 0 1 2-2H19a2 2 0 0 1 2 2z" />
              </svg>
              <input
                value={n.text}
                placeholder="Комментарий"
                onChange={(e) =>
                  patch(item.id, (i) => ({
                    notes: (i.notes ?? []).map((x) =>
                      x.id === n.id ? { ...x, text: e.target.value } : x
                    ),
                  }))
                }
                className={clsx(
                  'min-w-0 flex-1 bg-transparent text-[#575757] outline-none',
                  sub ? 'text-[11px]' : 'text-[11.5px]'
                )}
              />
              <button
                type="button"
                onClick={() =>
                  patch(item.id, (i) => ({
                    notes: (i.notes ?? []).filter((x) => x.id !== n.id),
                  }))
                }
                className="text-[12px] text-[rgba(20,30,49,0.42)]"
                aria-label="Удалить комментарий"
              >
                ×
              </button>
            </span>
          ))}
        </span>
      ) : null}

      {members.length > 0 ? (
        <span
          className={clsx(
            'order-8 mt-[7px] flex w-full flex-wrap gap-[6px]',
            padLeft
          )}
        >
          {members.map((id) => {
            const p = peopleById.get(id);
            const name = p?.name ?? `#${id}`;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-[6px] rounded-full bg-white py-[3px] pl-[3px] pr-[9px] shadow-[0_1px_3px_rgba(20,30,49,0.08)]"
              >
                <RPersonAvatar
                  id={id}
                  name={name}
                  avatarUrl={p?.avatarUrl}
                  size={sub ? 19 : 20}
                  fontSize={sub ? 9 : 9.5}
                />
                <span
                  className={clsx(
                    'whitespace-nowrap font-medium text-[#575757]',
                    sub ? 'text-[10.5px]' : 'text-[11px]'
                  )}
                >
                  {name}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    patch(item.id, (i) => ({
                      members: (i.members ?? []).filter((x) => x !== id),
                    }))
                  }
                  className="text-[12px] text-[rgba(20,30,49,0.42)]"
                  aria-label={`Убрать ${name}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </span>
      ) : null}

      {/* Подпункты */}
      {!sub && subs.length > 0 ? (
        <div className="order-9 ml-[28px] mt-[8px] flex w-full flex-col gap-[5px] rounded-[14px] bg-[#F6F7FA] px-[10px] py-[8px]">
          {subs.map((s) => (
            <RChecklistRow key={s.id} item={s} subs={[]} ctx={ctx} sub />
          ))}
        </div>
      ) : null}
    </div>
  );
}
